import {
  useAdsWorkspaceStore,
  useAdsResearchStore,
  isSyntheticLocalBananJob,
  type SubAgentId,
} from "@/features/ads";
import { ADS_SUB_AGENTS, type CompanionSubAgent } from "../../lib/chatAgentRegistry";

export function useSubAgentStatus() {
  const pendingKlingJobs = useAdsWorkspaceStore((s) => s.pendingKlingJobs);
  const pendingBananJobs = useAdsWorkspaceStore((s) => s.pendingBananJobs);
  const pendingHiggsfieldJobs = useAdsWorkspaceStore((s) => s.pendingHiggsfieldJobs);
  // Synthetic local-Banan start-image jobs have no managed-chat status record
  // (see `isSyntheticLocalBananJob`); a stray/persisted one would otherwise
  // pin the Banan badge "active" forever. Mirror RunningAgentsPanel's guard.
  const trackedBananJobs = pendingBananJobs.filter(
    (j) => !isSyntheticLocalBananJob(j.jobId),
  );
  const subAgentBusy = useAdsWorkspaceStore((s) => s.subAgentBusy);
  const revealedSubAgents = useAdsWorkspaceStore((s) => s.revealedSubAgents);
  const researchStatus = useAdsResearchStore((s) => s.status);

  // Scout is "active" while research is in flight. Completed/errored states
  // are surfaced separately by RunningAgentsPanel's own dismiss timer — the
  // hook keeps tight "currently working" semantics so the inline notifier
  // and header badges don't linger after the run finishes.
  const researchRunning = researchStatus === "running";

  function getJobCount(subAgentId: SubAgentId): number {
    if (subAgentId === "banan") {
      return trackedBananJobs.reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (subAgentId === "vid") {
      return pendingKlingJobs
        .filter((j) => j.provider !== "heygen")
        .reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (subAgentId === "agent_v") {
      return pendingKlingJobs
        .filter((j) => j.provider === "heygen")
        .reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (subAgentId === "higgsfield") {
      return pendingHiggsfieldJobs
        .filter((j) => j.generator !== "higgsfield_cli")
        .reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    if (subAgentId === "higgsfield_cli") {
      return pendingHiggsfieldJobs
        .filter((j) => j.generator === "higgsfield_cli")
        .reduce((sum, j) => sum + (j.itemCount ?? 1), 0);
    }
    return 0;
  }

  // Banan/Vid/Higgsfield have native polling cards; their "active" derives
  // from pending-job lists. Scout has the research store. Mads/GAds run
  // server-side with no polling — their busy flag is set on Marcus dispatch
  // (in useCompanionToolDispatch) and cleared either
  // when the user opens that agent's chat or by a 5-minute safety timeout.
  function isActive(subAgentId: SubAgentId): boolean {
    if (subAgentId === "banan") return trackedBananJobs.length > 0 || subAgentBusy.banan;
    if (subAgentId === "vid")
      return pendingKlingJobs.some((j) => j.provider !== "heygen") || subAgentBusy.vid;
    if (subAgentId === "higgsfield")
      return (
        pendingHiggsfieldJobs.some((j) => j.generator !== "higgsfield_cli") ||
        subAgentBusy.higgsfield
      );
    if (subAgentId === "higgsfield_cli")
      return (
        pendingHiggsfieldJobs.some((j) => j.generator === "higgsfield_cli") ||
        subAgentBusy.higgsfield_cli
      );
    if (subAgentId === "scout") return researchRunning || subAgentBusy.scout;
    if (subAgentId === "meta_ads") return subAgentBusy.meta_ads;
    if (subAgentId === "gads") return subAgentBusy.gads;
    if (subAgentId === "agent_v")
      return pendingKlingJobs.some((j) => j.provider === "heygen") || subAgentBusy.agent_v;
    return false;
  }

  const totalActiveJobs =
    pendingKlingJobs.length +
    trackedBananJobs.length +
    pendingHiggsfieldJobs.length +
    (researchRunning ? 1 : 0);

  const visibleAgents = ADS_SUB_AGENTS.filter(
    (a) => a.alwaysVisible || revealedSubAgents.includes(a.subAgentId),
  );

  return { agents: ADS_SUB_AGENTS, visibleAgents, getJobCount, isActive, totalActiveJobs };
}

export type SubAgent = CompanionSubAgent;
